import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { dhfApi } from '../../utils/dhfApi';
import { healthColor, utc } from './dhfUi';
import { DocTabs, REASONS, ReasonChip, downloadVariant } from './variantsUi';
import '../../styles/DocReview.css';
import '../../styles/Dhf.css';
import '../../styles/DhfVariants.css';

function Stat({ label, value, sub, tone, to }) {
  return (
    <div className="dhfv-stat">
      <div className="dhfv-stat-l">{label}</div>
      <div className={`dhfv-stat-n${tone ? ` dhfv-stat-n--${tone}` : ''}`}>{value ?? '—'}</div>
      {sub && (to ? <Link to={to} className="dhfv-stat-sub">{sub}</Link> : <div className="dhfv-stat-sub">{sub}</div>)}
    </div>
  );
}

/**
 * A DHF document's Branches tab (mockup screen 1): the Orcanos origin on top, one card per branch
 * (reason, health, changed / added / removed, how far behind its parent, how much was merged), and
 * the Fork dialog. Forking copies the outline into this app — nothing changes in Orcanos (D1, D2).
 */
export default function DhfVariantsPage() {
  const { setupId, docId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [forkOpen, setForkOpen] = useState(false);
  const [form, setForm] = useState({ name: '', reason: 'Regulation', note: '', source: 'origin' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await dhfApi.variants(setupId, docId));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [setupId, docId]);
  useEffect(() => { load(); }, [load]);

  const fork = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await dhfApi.fork(setupId, docId, form);
      nav(`/dhf/${setupId}/variant/${r.id}`);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const download = async (vid, fmt) => {
    try { await downloadVariant(setupId, vid, fmt); } catch (e) { setError(e.message); }
  };

  if (error && !data) return <div className="drv-page"><div className="drv-error" role="alert">{error}</div></div>;
  if (!data) return <div className="drv-page"><div className="drv-muted">Loading…</div></div>;
  const o = data.origin;
  const oh = o.score?.health;

  return (
    <div className="drv-page">
      <div className="dhf-doc-bar">
        <div className="dhf-doc-bar-l">
          <Link to={`/dhf/${setupId}?doc=${docId}`}>← DHF</Link>
          <strong>{o.name}</strong>
          {o.url ? <a className="dhf-key" href={o.url} target="_blank" rel="noopener noreferrer">{o.key} ↗</a> : <span>{o.key}</span>}
          <span className="dhf-chip" style={{ background: '#F4F1FB', color: healthColor(oh) }}>health {oh ?? '—'}</span>
        </div>
        <div className="dhf-doc-bar-r">
          {data.variants.length > 0 && (
            <Link className="drv-btn" to={`/dhf/${setupId}/doc/${docId}/variants/compare?a=origin&b=${data.variants[0].id}`}>Compare…</Link>
          )}
          <button type="button" className="drv-btn drv-btn--primary" onClick={() => setForkOpen(true)}>＋ Fork this document</button>
        </div>
      </div>
      <DocTabs setupId={setupId} docId={docId} active="branches" count={data.variants.length} />

      {error && <div className="drv-error" role="alert">{error}</div>}
      {!data.outline_ok && (
        <div className="dhf-warn">The document's outline could not be read from Orcanos ({data.outline_error || 'not read yet'}).
          Forking from the origin and pulling need it — use <em>Reload outline</em> on the Document tab.</div>
      )}

      <div className="dhfv-layout">
        <main className="dhfv-main">
          <p className="dhfv-lead">A branch is a copy of this document kept in this app. Orcanos is not changed until you
            <strong> merge</strong>. Each item shows the origin's traces and test results.</p>

          <div className="drv-card dhfv-card dhfv-card--origin">
            <div className="dhfv-card-h">
              <div className="dhfv-card-t">
                <span className="dhfv-origin-tag">ORIGIN</span>
                <strong>{o.name} — in Orcanos</strong>
                <span className="drv-muted">{o.key} · {o.n_items} items · built {utc(data.built_at)}</span>
              </div>
              <span className="dhf-chip" style={{ background: '#fff', color: healthColor(oh) }}>health {oh ?? '—'}</span>
            </div>
          </div>

          {data.variants.length === 0 && (
            <div className="drv-card dhfv-empty">
              No branches yet. Fork this document to keep a version for a country, a product model, a customer — any reason —
              and compare it with the origin at any time.
            </div>
          )}

          {data.variants.map((v) => {
            const h = v.score?.health;
            const base = `/dhf/${setupId}/variant/${v.id}`;
            return (
              <div key={v.id} className="drv-card dhfv-card">
                <div className="dhfv-card-h">
                  <div className="dhfv-card-t">
                    <Link to={base} className="dhfv-name">{v.name}</Link>
                    <ReasonChip reason={v.reason} />
                    <span className="drv-muted">forked from {v.parent_id ? <strong>{v.parent_label}</strong> : 'Origin'} · {utc(v.created_at)}{v.created_by_name ? ` by ${v.created_by_name}` : ''}</span>
                  </div>
                  <span className="dhf-chip" style={{ background: '#F4F1FB', color: healthColor(h) }}>health {h ?? '—'}</span>
                </div>
                {v.note && <p className="dhfv-note">{v.note}</p>}
                <div className="dhfv-stats">
                  <Stat label="Changed" value={v.counts.changed} sub={v.counts.conflicts ? `${v.counts.conflicts} conflict${v.counts.conflicts > 1 ? 's' : ''}` : null}
                        tone={v.counts.conflicts ? 'warn' : null} />
                  <Stat label="Added" value={v.counts.added}
                        sub={v.counts.added_untraced ? `${v.counts.added_untraced} not traced` : null} />
                  <Stat label="Removed" value={v.counts.removed} />
                  <Stat label={v.parent_id ? 'Parent changed since' : 'Origin changed since'} value={v.behind}
                        tone={v.behind ? 'warn' : 'good'}
                        sub={v.behind == null ? 'origin not readable' : v.behind ? `Pull from ${v.parent_label} →` : 'up to date'}
                        to={v.behind ? `${base}/pull` : null} />
                  <Stat label="Merged to Orcanos" value={v.merged} />
                </div>
                <div className="dhfv-actions">
                  <Link className="drv-btn drv-btn--primary" to={base}>Open</Link>
                  <Link className="drv-btn" to={`/dhf/${setupId}/doc/${docId}/variants/compare?a=${v.parent_id || 'origin'}&b=${v.id}`}>Compare</Link>
                  <Link className="drv-btn" to={`${base}/pull`}>Pull{v.behind ? ` · ${v.behind}` : ''}</Link>
                  <Link className="drv-btn" to={`${base}/merge`}>Merge to Orcanos…</Link>
                  <button type="button" className="drv-btn" onClick={() => download(v.id, 'docx')}>Export Word</button>
                  <button type="button" className="drv-btn" onClick={() => download(v.id, 'pdf')}>Export PDF</button>
                </div>
              </div>
            );
          })}
        </main>

        {forkOpen && (
          <form className="drv-card dhfv-fork" role="dialog" aria-label="Fork this document" onSubmit={fork}>
            <div className="dhfv-fork-h">
              <strong>Fork this document</strong>
              <button type="button" className="dhfv-x" aria-label="Close" onClick={() => setForkOpen(false)}>×</button>
            </div>
            <label className="dhfv-field">Name
              <input type="text" required maxLength={120} value={form.name} autoFocus
                     placeholder="e.g. EU — MDR, Model B, Customer X"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label className="dhfv-field">Reason
              <input type="text" maxLength={60} list="dhfv-reasons" value={form.reason}
                     onChange={(e) => setForm({ ...form, reason: e.target.value })} />
              <datalist id="dhfv-reasons">{REASONS.map((r) => <option key={r} value={r} />)}</datalist>
            </label>
            <div className="dhfv-reason-picks">
              {REASONS.map((r) => (
                <button key={r} type="button" className={`dhfv-pick${form.reason === r ? ' dhfv-pick--on' : ''}`}
                        onClick={() => setForm({ ...form, reason: r })}>{r}</button>
              ))}
            </div>
            <label className="dhfv-field">Fork from
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                <option value="origin">Origin — {o.key} (Orcanos)</option>
                {data.variants.map((v) => <option key={v.id} value={v.id}>{v.name} (branch)</option>)}
              </select>
            </label>
            <label className="dhfv-field">Note
              <textarea rows={3} maxLength={2000} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </label>
            <div className="dhf-note">
              Copies the outline and the text of <strong>{o.n_items} items in {o.paragraphs} paragraphs</strong> into this app.
              Traces stay the origin's. <strong>Nothing changes in Orcanos.</strong>
            </div>
            <div className="dhfv-fork-f">
              <button type="button" className="drv-btn" onClick={() => setForkOpen(false)}>Cancel</button>
              <button type="submit" className="drv-btn drv-btn--primary" disabled={busy || !form.name.trim()}>
                {busy ? 'Creating…' : 'Create branch'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
